import React from "react";
import { ArrowUp } from "lucide-react";
import logger from "@/lib/logger";
import { isInterruptedRequestError } from "@/main/common/ApiUtils";
import { scrollElementIntoViewWithOffset } from "@/main/common/ScrollUtils";
import { onNativeResume } from "@/main/mobile-shell/nativeResume";
import { MobileShellRuntime } from "@/main/mobile-shell/MobileShellRuntime";

/** The bridge's resume threshold (`MobileDeepLinkBridge` `RESUME_REFRESH_THRESHOLD_MS`), reused for the web path. */
export const WEB_TAB_RETURN_THRESHOLD_MS = 60_000;

/**
 * FI-589: "a web tab came back after a real absence" — `visibilitychange` → visible after the tab was hidden for at
 * least {@link WEB_TAB_RETURN_THRESHOLD_MS}. A shorter switch is tab-hopping, not coming back. Inert inside the native
 * shell, where the bridge's `onNativeResume` already covers the same moment. Returns the unsubscribe.
 */
export function onWebTabReturn(listener: () => void): () => void {
    if (typeof document === "undefined" || MobileShellRuntime.isNativeShell()) {
        return () => {};
    }
    let hiddenAtMs: number | null = document.visibilityState === "hidden" ? Date.now() : null;
    const handleVisibilityChange = () => {
        if (document.visibilityState === "hidden") {
            hiddenAtMs = Date.now();
            return;
        }
        if (document.visibilityState !== "visible" || hiddenAtMs == null) {
            return;
        }
        const hiddenForMs = Date.now() - hiddenAtMs;
        hiddenAtMs = null;
        if (hiddenForMs >= WEB_TAB_RETURN_THRESHOLD_MS) {
            listener();
        }
    };
    document.addEventListener("visibilitychange", handleVisibilityChange);
    return () => {
        document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
}

/**
 * The native-resume head check (round 12 slice 1m, D11/D28/A19), shared by `PaginatedListWrapperV2` and the lists
 * that walk their own paging (`SeriesDiscussionList`). Design:
 * `designs/01-now/2026-07-28-native-deep-links-lifecycle-and-route-restoration-design.md` §5 "Lifecycle".
 *
 * On a native resume (`onNativeResume`, the bridge's 60s policy) `fetchFresh` runs in the background and its FIRST
 * item's id is compared with the list's:
 * <ul>
 *   <li>same head: nothing is written, nothing re-renders;</li>
 *   <li>new head, list top in view: `swapIn(fresh)` replaces the list silently;</li>
 *   <li>new head, reader scrolled into the list: the fresh items are held and {@link NativeResumeNewItemsPill}
 *       offers them; tapping swaps them in and scrolls the list's top into view. A second resume whose head matches
 *       the held items writes nothing.</li>
 * </ul>
 */
export function useNativeResumeHeadCheck<T>(options: {
    /** Subscribe at all. `false` means the list is bit-for-bit unchanged. */
    enabled: boolean;
    /** FI-589: also run on a web tab return ({@link onWebTabReturn}). Only meaningful when `enabled`. */
    onWebTabReturn?: boolean;
    /** The background fetch. An empty result is a no-op (a superseded check may return `[]` on purpose). */
    fetchFresh: () => Promise<T[]>;
    getId: (item: T) => string | undefined;
    getCurrentItems: () => T[];
    /** `true` while the check must not run (loading, an owner editing the list, ...). */
    shouldSkip: () => boolean;
    /** Replace the list with the fresh items. The held items are cleared by the hook, not here. */
    swapIn: (fresh: T[]) => void;
    containerRef: React.RefObject<HTMLElement | null>;
    logLabel: string;
}): { heldFreshItems: T[] | null; showHeldFreshItems: () => void; clearHeldFreshItems: () => void } {
    const { enabled, fetchFresh, getId, getCurrentItems, shouldSkip, swapIn, containerRef, logLabel } = options;
    const webTabReturn = options.onWebTabReturn === true;

    // Only ever set to items whose head differs from the list's, and never re-set to items with the same head, so a
    // repeated resume with no news is a no-op write.
    const [heldFreshItems, setHeldFreshItems] = React.useState<T[] | null>(null);
    const heldFreshItemsRef = React.useRef<T[] | null>(null);
    const checkInFlightRef = React.useRef(false);
    const isMountedRef = React.useRef(true);

    React.useEffect(() => {
        isMountedRef.current = true;
        return () => {
            isMountedRef.current = false;
        };
    }, []);

    const holdFreshItems = React.useCallback((items: T[] | null) => {
        heldFreshItemsRef.current = items;
        setHeldFreshItems(items);
    }, []);

    const clearHeldFreshItems = React.useCallback(() => {
        if (heldFreshItemsRef.current !== null) {
            holdFreshItems(null);
        }
    }, [holdFreshItems]);

    const swapInFresh = React.useCallback((fresh: T[]) => {
        swapIn(fresh);
        clearHeldFreshItems();
    }, [clearHeldFreshItems, swapIn]);

    /**
     * "Scrolled to the top" for a list that is one block on a longer page: the list's own top edge is not above the
     * viewport's top, so the reader has not started reading into it and a swap moves nothing they are reading.
     */
    const isListTopInView = React.useCallback(() => {
        const node = containerRef.current;
        return node == null || node.getBoundingClientRect().top >= 0;
    }, [containerRef]);

    const checkForNewHead = React.useCallback(async () => {
        if (checkInFlightRef.current || shouldSkip() || getCurrentItems().length === 0) {
            return;
        }
        checkInFlightRef.current = true;
        try {
            const fresh = (await fetchFresh()).filter(Boolean);
            if (!isMountedRef.current || fresh.length === 0) {
                return;
            }
            const freshHead = getId(fresh[0]);
            const current = getCurrentItems();
            if (freshHead === undefined || current.length === 0 || freshHead === getId(current[0])) {
                return;
            }
            const held = heldFreshItemsRef.current;
            if (held !== null && held.length > 0 && getId(held[0]) === freshHead) {
                return;
            }
            if (isListTopInView()) {
                swapInFresh(fresh);
            } else {
                holdFreshItems(fresh);
            }
        } catch (err) {
            if (!isInterruptedRequestError(err)) {
                logger.warn(`[${logLabel}] native-resume head check failed`, err);
            }
        } finally {
            checkInFlightRef.current = false;
        }
    }, [fetchFresh, getCurrentItems, getId, holdFreshItems, isListTopInView, logLabel, shouldSkip, swapInFresh]);

    React.useEffect(() => {
        if (!enabled) {
            return;
        }
        const unsubscribeNative = onNativeResume(() => {
            void checkForNewHead();
        });
        const unsubscribeWeb = webTabReturn
            ? onWebTabReturn(() => {
                void checkForNewHead();
            })
            : () => {};
        return () => {
            unsubscribeNative();
            unsubscribeWeb();
        };
    }, [checkForNewHead, enabled, webTabReturn]);

    const showHeldFreshItems = React.useCallback(() => {
        const held = heldFreshItemsRef.current;
        if (held === null) {
            return;
        }
        swapInFresh(held);
        const node = containerRef.current;
        if (node) {
            void scrollElementIntoViewWithOffset(node, { attempts: 1 }).catch(() => {
                /* the swap already happened; a failed scroll leaves the reader where they were */
            });
        }
    }, [containerRef, swapInFresh]);

    return { heldFreshItems, showHeldFreshItems, clearHeldFreshItems };
}

/**
 * The "New posts" / "New replies" pill. Pinned just under the fixed app header (the same offset
 * `SearchPaginatedList` uses), so it stays reachable wherever the reader is in the list, and it takes no layout
 * space: the content under the reader does not move when it appears. `label` is already localized by the consumer.
 */
export function NativeResumeNewItemsPill(props: { label: string; onClick: () => void }) {
    return (
        <div className="pointer-events-none sticky top-[calc(4.5rem+env(safe-area-inset-top)+var(--seavien-connection-banner-height,0px))] z-10 flex h-0 justify-center overflow-visible">
            <button
                type="button"
                onClick={props.onClick}
                className="pointer-events-auto mt-2 inline-flex items-center gap-1.5 rounded-full border border-border bg-background px-3 py-1.5 text-sm font-medium text-foreground hover:bg-accent focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-ring/50"
                data-playwright-id="list-resume-new-items"
            >
                <ArrowUp className="h-4 w-4" aria-hidden="true" />
                {props.label}
            </button>
        </div>
    );
}
